import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const API_BASE_URL =
  process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL || "http://localhost:4009";

// Async thunk for fetching user guides
export const fetchUserGuides = createAsyncThunk(
  "userGuides/fetchUserGuides",
  async (
    {
      page = 1,
      limit = 10,
      search = "",
      sortBy = "created_at",
      sortOrder = "desc",
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: limit.toString(),
        ...(search && { search }),
        ...(sortBy && { sortBy }),
        ...(sortOrder && { sortOrder }),
      });

      const response = await fetch(
        `${API_BASE_URL}/api/user-guides?${queryParams}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return {
          guides: result.data || [],
          total: result.total || 0,
          page: result.page || page,
          pageSize: result.pageSize || limit,
        };
      } else {
        return rejectWithValue(result.message || "Failed to fetch user guides");
      }
    } catch (error) {
      return rejectWithValue("Error fetching data: " + error.message);
    }
  }
);

// Async thunk for deleting a user guide
export const deleteUserGuide = createAsyncThunk(
  "userGuides/deleteUserGuide",
  async (guideId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/user-guides/${guideId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return guideId;
      } else {
        return rejectWithValue(result.message || "Failed to delete user guide");
      }
    } catch (error) {
      return rejectWithValue("Error deleting guide: " + error.message);
    }
  }
);

// Async thunk for duplicating a user guide
export const duplicateUserGuide = createAsyncThunk(
  "userGuides/duplicateUserGuide",
  async (guideId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/user-guides/${guideId}/duplicate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        return rejectWithValue(
          result.message || "Failed to duplicate user guide"
        );
      }
    } catch (error) {
      return rejectWithValue("Error duplicating guide: " + error.message);
    }
  }
);

const userGuidesSlice = createSlice({
  name: "userGuides",
  initialState: {
    guides: [],
    loading: false,
    error: null,
    searchTerm: "",
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    sortBy: "createdAt",
    sortOrder: "desc",
  },
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
      state.currentPage = 1; // Reset to first page when search changes
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload;
      state.currentPage = 1; // Reset to first page when items per page changes
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserGuides.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserGuides.fulfilled, (state, action) => {
        state.loading = false;
        state.guides = action.payload.guides;
        state.totalItems = action.payload.total;
        state.currentPage = action.payload.page;
        state.itemsPerPage = action.payload.pageSize;
        state.error = null;
      })
      .addCase(fetchUserGuides.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteUserGuide.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserGuide.fulfilled, (state, action) => {
        state.loading = false;
        state.guides = state.guides.filter(
          (guide) => guide._id !== action.payload
        );
        state.totalItems = state.totalItems - 1;
        state.error = null;
      })
      .addCase(deleteUserGuide.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(duplicateUserGuide.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicateUserGuide.fulfilled, (state, action) => {
        state.loading = false;
        state.guides = [action.payload, ...state.guides];
        state.totalItems = state.totalItems + 1;
        state.error = null;
      })
      .addCase(duplicateUserGuide.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSearchTerm,
  setCurrentPage,
  setItemsPerPage,
  setSortBy,
  setSortOrder,
  clearError,
} = userGuidesSlice.actions;

export default userGuidesSlice.reducer;
